import { HttpBackend, HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, catchError, filter, from, map, mergeMap, of, retry, Subscription, switchMap, tap, toArray, firstValueFrom } from 'rxjs';

// unused import to include the worker in the build
import { FILES_CONCURRENCY, TIME_UPDATE_INTERVAL, MULTIPART_CONCURRENCY, RETRY_COUNT, RETRY_DELAY, DICOM_ACQUISITION_ERROR } from './local-import.constants';
import { ConfigService } from '../../config.service';
import { createWorker } from './de-id-web-worker-init';
import { } from './de-id-web-worker.worker';
import { CoreApiService } from '../../core-api.service';
import { FileStatus, FileTypes, UploaderWorkerEnum } from './local-import.enum';
import { FileWithPath, ImportFileInfo, UploadChunkResponse } from './local-import.interface';
import { areFilesEqual, areFilesInSameDir, splitFileIntoChunks } from './local-import.utils';
import { LoggingService } from '../../logging.service';
import { DeidProfile } from '../../shared/models/dicom.model';
import { XferBlob, XferBlobPart, XferBlobUploadResponse, XferPendingBlob } from '../../shared/models/xfer.model';

@Component({
  host: { 'test-id': 'local-import' },
  selector: 'local-import',
  styleUrls: ['./local-import.component.scss'],
  templateUrl: './local-import.component.html',
})
export class LocalImport implements OnInit, OnChanges, OnDestroy {
  @Input() initialCompletedFiles = 0;
  @Input() initialFailedFiles = 0;
  @Input() initialFilesInfo: ImportFileInfo[] = [];
  @Input() initialUploadedBlobsIds: Record<string, string[]> = {};
  @Input() initialPrefix: string;
  @Input() projectId: string;
  @Input() isDragAndDropDisabled = false;
  @Input() blobSessionId: string;
  @Input() deidProfile: DeidProfile;

  @Output() readonly updateBlobSessionId = new EventEmitter<string>()
  @Output() readonly updateCompletedFiles = new EventEmitter<number>()
  @Output() readonly updateIsRemovingBlobs = new EventEmitter<boolean>()
  @Output() readonly updateFailedFiles = new EventEmitter<number>()
  @Output() readonly updateUploadedBlobsIds = new EventEmitter<Record<string, string[]>>()
  @Output() readonly updateFilesInfo = new EventEmitter<ImportFileInfo[]>()

  completedFiles: number;
  failedFiles: number;
  filesInfo: ImportFileInfo[];
  failedDicomFiles = 0;
  filesToProcess$ = new Subject<ImportFileInfo>();
  progressBarValue = 0;
  remainingTimeMessage: number;
  totalSizesProcessed = 0;
  totalTimeProcessed = 0;
  protected isDropAreaActive = false;
  private readonly workerInitializationError =
    'Web Worker not supported or initialized.'
  public dcmjs: any // eslint-disable-line @typescript-eslint/no-explicit-any

  private http: HttpClient;
  private subscriptions = {
    processFiles: Subscription.EMPTY,
  };
  private removedFilesNames: Set<string> = new Set();
  private uploadedBlobsIds: Record<string, string[]>;
  private lastRemainingTimeCalculation = 0;
  private workersPool: Worker[] = [];

  constructor(
    private config: ConfigService,
    private handler: HttpBackend,
    private coreApi: CoreApiService,
    private logger: LoggingService,
  ) {
    if (typeof Worker !== 'undefined') {
      for (let i = 0; i < FILES_CONCURRENCY; i++) {
        const worker = createWorker();
        this.workersPool.push(worker);
      }
    }
  }

  ngOnInit(): void {
    this.completedFiles = this.initialCompletedFiles;
    this.failedFiles = this.initialFailedFiles;
    this.filesInfo = this.initialFilesInfo;
    this.uploadedBlobsIds = this.initialUploadedBlobsIds;

    this.http = new HttpClient(this.handler);
    this.subscriptions.processFiles = this.createFileToProcessSubscription();
    this.updateProgress();
    for (const fileInfo of this.filesInfo) {
      this.filesToProcess$.next(fileInfo);
    }

    import(/* webpackChunkName: "dcmjs" */ '../../upload/dependencies').then(lazy => {
      this.dcmjs = lazy.dcmjs;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deidProfile?.currentValue) {
      this.workersPool.forEach((worker) => {
        void this.setProfileInWorker(this.deidProfile, worker)
      })
    }
  }

  ngOnDestroy(): void {
    for (const key in this.subscriptions) {
      this.subscriptions[key].unsubscribe();
    }

    for (const worker of this.workersPool) {
      worker.terminate();
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.isDragAndDropDisabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isDropAreaActive = true;
    const element = event.currentTarget as HTMLInputElement;

    if (!element.classList.contains('active')) {
      element.classList.add('active');
    }
  }

  onDragLeave(event: DragEvent): void {
    if (this.isDragAndDropDisabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDropAreaActive = false;
  }

  onDropFiles(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDropAreaActive = false;
    const entries = Array.from(event.dataTransfer.items).map(item => item.webkitGetAsEntry());

    this.collectUploadedFiles(entries).then((files) => this.processFiles(files));
  }

  async collectUploadedFiles(entries: FileSystemEntry[]): Promise<File[]> {
    const files = [];
    for (const entry of entries) {
      if (entry.isDirectory) {
        files.push(...await this.readDirectoryEntries(entry as FileSystemDirectoryEntry));
      } else if (entry.isFile) {
        files.push(await this.getFile(entry as FileSystemFileEntry));
      }
    }
    return files;
  }

  async readDirectoryEntries(entry: FileSystemDirectoryEntry): Promise<File[]> {
    const reader = entry.createReader();
    let entries = await this.readEntries(reader);
    const files = [];
    while (entries.length > 0) {
      files.push(...await this.collectUploadedFiles(entries));
      entries = await this.readEntries(reader);
    }
    return files;
  }

  readEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
    return new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  }

  getFileCount(status: FileStatus): number {
    return this.filesInfo.filter(file => file.status === status).length;
  }

  getFile(entry: FileSystemFileEntry): Promise<File> {
    return new Promise((resolve, reject) => {
      entry.file(
        file => {
          (file as FileWithPath).fullPath = entry.fullPath.substring(1)
          resolve(file);
        },
        error => {
          this.logger.error(error);
          reject(error);
        }
      );
    });
  }

  async onSelectFiles(event: Event): Promise<void> {
    const files = this.getFilesFromEvent(event);
    this.processFiles(Array.from(files) as FileWithPath[])
  }

  getFilesFromEvent(event: Event): FileList {
    return (event.target as HTMLInputElement).files;
  }

  /**
   *
   * @param fileToUpload a File to be 'chunked' into pieces, and if a dicom file, to firstly be parsed and de-identified
   * @returns chunks - the file that has been 'chunked' into pieces
   * @returns dicomError - the error message to associate with the dicom file if it fails to be parsed or de-identified
   */
  async chunkFile(fileToUpload: ImportFileInfo): Promise<{ chunks: Blob[], dicomError: string }> {
    let dicomError = '';
    let fileToChunk = fileToUpload.file;
    const worker = this.workersPool.pop();

    if (worker) {
      const response = await this.deidFileWithWorker(fileToUpload, worker)
      fileToUpload.fileType = response.fileType;
      dicomError = response.readError || response.deidError;
      if (!dicomError) {
        fileToChunk = response.file
      }
      this.workersPool.push(worker)
    }

    if (!dicomError) {
      const chunks: Blob[] = splitFileIntoChunks(fileToChunk)
      return { chunks, dicomError }
    }

    return { chunks: [], dicomError }
  }

  /**
   * Determine 'leaf' folders, and if a leaf contains a failed dicom file, mark all sibling files as errors (acquisition policy)
   * @param allFiles an array of files
   */
  private handleFailedDicomLeafs(allFiles: ImportFileInfo[]): void {
    if (this.failedDicomFiles > 0) {
      const foldersMarkedAsLeafs: string[] = []
      for (const thisFile of allFiles) {
        // get thisFile folder path
        const thisFolderPath = thisFile.fileName.substring(0, thisFile.fileName.lastIndexOf('/') + 1)
        // if this files folder has not been marked as a leaf and has a dicom parse error
        if (foldersMarkedAsLeafs.includes(thisFolderPath) === false && thisFile.isDicomFailure) {
          const thisFolderIsALeaf = !allFiles.some(file => {
            const isSameFolderBranch = file.fileName.includes(thisFolderPath)
            const isFurtherDownTheBranch = file.fileName.lastIndexOf('/') > thisFile.fileName.lastIndexOf('/')
            return isSameFolderBranch && isFurtherDownTheBranch
          })
          if (!thisFolderIsALeaf) {
            continue;
          }
          foldersMarkedAsLeafs.push(thisFolderPath);

          this.markSiblingDicomsFailed(thisFile);
          this.updateProgress()
        }
      }
    }
  }

  private markSiblingDicomsFailed(currentFile: ImportFileInfo): void {
    this.filesInfo
      .forEach(file => {
        if (file.fileName.endsWith(FileTypes.Dcm) && areFilesInSameDir(currentFile, file)) {
          file.isDicomFailure = true
          file.status = FileStatus.Error

          if (file.failureReason === '') {
            file.failureReason = DICOM_ACQUISITION_ERROR
          }
        }
      });
  }

  async processFiles(files: FileWithPath[]): Promise<void> {
    if (this.subscriptions.processFiles && !this.filesInfo.length) {
      this.removedFilesNames.clear();
      this.subscriptions.processFiles.unsubscribe();
      this.subscriptions.processFiles = this.createFileToProcessSubscription();
    }

    for (const file of files) {
      if (file.name.startsWith('.') || !file.size) {
        continue;
      }

      const fileName = this.removeParentDirFromFilePath(file.fullPath || file.webkitRelativePath || file.name);
      const fileInfo: ImportFileInfo = {
        status: FileStatus.InProgress,
        fileName,
        size: file.size,
        actions: '',
        file: file,
        isDicomFailure: false,
        failureReason: '',
      };

      this.removedFilesNames.delete(fileInfo.fileName);

      // only add file if not added yet
      if (this.isFileShouldBeAdded(fileInfo)) {
        this.filesInfo.push(fileInfo)
      } else {
        if (this.isFileShouldBeUpdated(fileInfo)) {
          this.updateFileForUploadingProcess(fileInfo);
        }
      }
    }

    this.updateFilesInfo.emit(this.filesInfo);

    for (const file of this.filesInfo) {
      this.filesToProcess$.next(file);
    }

    this.updateProgress();
  }

  processFile(fileInfo: ImportFileInfo, chunks: Blob[]): Observable<XferBlob | null> {
    if (!this.isFileShouldBeUploaded(fileInfo)) {
      return of(null);
    }
    const isMultipart = chunks.length > 1;
    return this.coreApi.xfer.blobs.create({
      project_id: this.projectId,
      path: `${fileInfo.fileName}`,
      blob_session_id: this.blobSessionId,
    }, { multipart: isMultipart }).pipe(
      filter(() => this.isFileShouldBeUploaded(fileInfo)),
      switchMap((blob) => this.uploadChunks(fileInfo, chunks, blob, isMultipart)),
      filter((data) => this.isFileShouldBeUploaded(data.fileInfo)),
      switchMap((data) => {
        if (data) {
          const { blobId, parts, fileInfo } = data;
          return this.finishBlob(fileInfo, blobId, isMultipart ? parts : null);
        }
        return null;
      }),
    );
  }

  uploadChunks(fileInfo: ImportFileInfo, chunks: Blob[], blob: XferPendingBlob, isMultipart: boolean): Observable<UploadChunkResponse | null> {
    if (!this.isFileShouldBeUploaded(fileInfo)) {
      return of(null);
    }

    fileInfo.completedPartsSize = 0;

    return from(chunks).pipe(
      mergeMap((chunk, index) => this.uploadBlobs(fileInfo, chunk, blob, index, isMultipart ? index + 1 : null), MULTIPART_CONCURRENCY),
      toArray(),
      map(parts => {
        if (parts.some(part => !part)) {
          return null;
        }

        return ({ blobId: blob._id, parts: parts.sort((a, b) => a?.part - b?.part), fileInfo: fileInfo });
      }),
    );
  }

  uploadBlobs(fileInfo: ImportFileInfo, chunk: Blob, blob: XferPendingBlob, index: number, part?: number): Observable<XferBlobPart | null> {
    if (!this.isFileShouldBeUploaded(fileInfo)) {
      return of(null);
    }

    return this.coreApi.xfer.blobs.upload(blob._id, part ? { part } : null).pipe(
      switchMap((uploadBlobResponse) => this.uploadChunk(chunk, uploadBlobResponse, fileInfo)),
      tap((response) => {
        if (response.status >= 200 && response.status < 300) {

          fileInfo.completedPartsSize += chunk.size;

          if (fileInfo.completedPartsSize !== fileInfo.size) {
            this.calculateRemainingTime();
          }

          this.updateData();
        }
      }),
      map((response) => ({
        etag: response.headers.get('Etag') ? response.headers.get('Etag').split('"')[1] : null,
        part: index + 1,
      })),
    );
  }

  // indeterminable type from storage bucket url
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadChunk(chunk: Blob, blob: XferBlobUploadResponse, fileInfo: ImportFileInfo): Observable<any> {
    const startTime = new Date().getTime();
    return this.http.put(blob.upload_url, chunk, { observe: 'response', headers: blob.upload_headers }).pipe(
      tap((response) => {
        if (response.status >= 200 && response.status < 300 && fileInfo.status === FileStatus.InProgress) {
          const endTime = new Date().getTime();
          const elapsedTime = endTime - startTime;
          this.totalTimeProcessed += elapsedTime;
          this.totalSizesProcessed += chunk.size;
        }
      }),
    );
  }

  finishBlob(fileInfo: ImportFileInfo, blobId: string, parts?: XferBlobPart[]): Observable<XferBlob> {
    return this.coreApi.xfer.blobs.finish(blobId, parts ? { parts } : null).pipe(
      tap(async () => {
        this.addBlobId(fileInfo.fileName, blobId);
        this.updateUploadedBlobsIds.emit(this.uploadedBlobsIds);
        fileInfo.status = FileStatus.Complete;

        if (this.removedFilesNames.has(fileInfo.fileName)) {
          await this.handleCancel([fileInfo]);
        }

        this.calculateRemainingTime();
        this.updateProgress();
      }),
    );
  }

  protected handleRetry(files: ImportFileInfo[]): void {
    files.forEach((file) => {
      file.status = FileStatus.InProgress;
      this.filesToProcess$.next(file);
    });
    this.updateProgress();
  }

  protected async handleCancel(files: ImportFileInfo[]): Promise<void> {
    const removedFiles = this.getRemovedBlobsIds(files);
    files.forEach((currentFile) => {
      this.removedFilesNames.add(currentFile.fileName);
      const canceledFile = this.filesInfo.find(file => file === currentFile);

      if (canceledFile) {
        canceledFile.status = FileStatus.Cancel;
        this.filesInfo = this.filesInfo.filter(file => file !== canceledFile);
        this.totalSizesProcessed -= canceledFile.completedPartsSize ?? 0;
        this.calculateRemainingTime();
      }
    });
    this.updateProgress();
    await this.removeXferBlobs(removedFiles);
  }

  updateProgress(): void {
    this.completedFiles = this.getFileCount(FileStatus.Complete);
    this.failedFiles = this.getFileCount(FileStatus.Error);
    this.failedDicomFiles = this.filesInfo.filter(file => file.isDicomFailure).length;
    const processedFiles = this.completedFiles + this.failedFiles;
    this.progressBarValue = Math.round(this.filesInfo.length === 0 ? 0 : (processedFiles / this.filesInfo.length) * 100);
    this.updateUploadedBlobsIds.emit(this.uploadedBlobsIds);
    this.updateCompletedFiles.emit(this.completedFiles);
    this.updateFailedFiles.emit(this.failedFiles);
    this.updateData();
  }

  calculateRemainingTime(): void {
    const averageTimeBySizeProcessed = this.totalTimeProcessed / this.totalSizesProcessed;
    const remainingSizes = this.getTotalSizeOfAllFiles() - this.totalSizesProcessed;
    const remainingTimeMessage = averageTimeBySizeProcessed * remainingSizes;
    const currentTime = new Date().getTime();

    if (!remainingTimeMessage || currentTime - this.lastRemainingTimeCalculation > TIME_UPDATE_INTERVAL) {
      this.lastRemainingTimeCalculation = currentTime;
      this.remainingTimeMessage = remainingTimeMessage;
    }

    this.updateProgress();
  }

  getTotalSizeOfAllFiles(): number {
    return this.filesInfo.reduce((total, file) => total + file.size, 0);
  }

  readFile(file: FileWithPath | Blob): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = reject;
      fileReader.readAsArrayBuffer(file);
    });
  }

  private createFileToProcessSubscription(): Subscription {
    return this.filesToProcess$.pipe(
      filter(fileInfo => fileInfo.status === FileStatus.InProgress),
      mergeMap(fileInfo => {
        fileInfo.completedPartsSize = 0;

        if (!this.isFileShouldBeUploaded(fileInfo)) {
          return of();
        }

        return from(this.chunkFile(fileInfo)).pipe(
          switchMap(({ chunks, dicomError }) => {
            fileInfo.status = dicomError ? FileStatus.Error : fileInfo.status;
            fileInfo.isDicomFailure = Boolean(dicomError);
            fileInfo.failureReason = dicomError;

            if (fileInfo.status !== FileStatus.InProgress) {
              this.updateProgress();
              this.handleFailedDicomLeafs(this.filesInfo);
              this.updateProgress();
              return of();
            }

            return this.processFile(fileInfo, chunks).pipe(
              retry({ count: RETRY_COUNT, delay: RETRY_DELAY }),
              catchError(() => {
                fileInfo.status = FileStatus.Error;
                this.updateProgress();
                return of();
              })
            );
          })
        );
      }, FILES_CONCURRENCY),
    ).subscribe();
  }

  private isFileShouldBeUploaded(fileInfo: ImportFileInfo): boolean {
    return !this.removedFilesNames.has(fileInfo.fileName) && fileInfo.status === FileStatus.InProgress;
  }

  private isFileShouldBeAdded(newFileInfo: ImportFileInfo): boolean {
    return !this.filesInfo.find((value) => value.fileName === newFileInfo.fileName);
  }

  private isFileShouldBeUpdated(newFileInfo: ImportFileInfo): boolean {
    const existingFileInfo = this.filesInfo.find((value) => value.fileName === newFileInfo.fileName);
    return !areFilesEqual(newFileInfo, existingFileInfo);
  }

  private updateFileForUploadingProcess(newFileInfo: ImportFileInfo): void {
    const existingFileIndex = this.filesInfo.findIndex((value) => value.fileName === newFileInfo.fileName);
    const existingFileInfo = this.filesInfo[existingFileIndex];

    if (existingFileInfo) {
      existingFileInfo.status = FileStatus.Cancel;
      this.filesInfo[existingFileIndex] = newFileInfo;
    }

    this.filesInfo = this.filesInfo.map(file => file.fileName === newFileInfo.fileName ? newFileInfo : file);
  }

  private removeParentDirFromFilePath(filePath: string): string {
    const splitFileName = filePath.split('/');
    return splitFileName.length < 2 ? filePath : splitFileName.slice(1).join('/');
  }

  private updateData(): void {
    this.filesInfo = [...this.filesInfo];
    this.updateFilesInfo.emit(this.filesInfo);
  }

  private addBlobId(name: string, id: string): void {
    if (this.uploadedBlobsIds[name]) {
      this.uploadedBlobsIds[name].push(id);
      return;
    }

    this.uploadedBlobsIds[name] = [id];
  }

  private getRemovedBlobsIds(files: ImportFileInfo[]): string[] {
    const removedIds = [];

    files.forEach((file) => {
      if (file.status === FileStatus.Complete) {
        this.uploadedBlobsIds[file.fileName]?.forEach((id) => removedIds.push(id));
        delete this.uploadedBlobsIds[file.fileName];
        this.updateUploadedBlobsIds.emit(this.uploadedBlobsIds);
      }
    });

    return removedIds;
  }

  private async removeXferBlobs(removedIds: string[]): Promise<void> {
    const promises = [];
    removedIds.forEach((id) => promises.push(firstValueFrom(this.coreApi.xfer.blobs.delete(id))));
    this.updateIsRemovingBlobs.emit(true);
    await Promise.all(promises);
    this.updateIsRemovingBlobs.emit(false);
  }

  private async setProfileInWorker(
    profile: DeidProfile,
    worker: Worker,
  ): Promise<File | null> {
    return new Promise<File | null>((resolve, reject) => {
      if (!worker) {
        return reject(this.workerInitializationError)
      }
      const message = { profile, type: UploaderWorkerEnum.SetProfile }

      worker.onmessage = ({ data }) => {
        if (data.isError) {
          resolve(null)
        } else {
          resolve(data)
        }
      }

      worker.onerror = (error) => {
        reject(`Worker error: ${error.message}`)
      }

      worker.postMessage(message)
    })
  }

  async deidFileWithWorker(
    file: ImportFileInfo,
    worker: Worker,
  ): Promise<{
    file: File
    fileType?: string
    readError?: string
    deidError?: string
  }> {
    return new Promise<{
      file: File
      fileType?: string
      readError?: string
      deidError?: string
    }>((resolve, reject) => {
      if (!worker) {
        return reject(this.workerInitializationError)
      }
      const message = { file, type: UploaderWorkerEnum.DeidDicom }

      worker.onmessage = ({ data }) => {
        resolve(data)
      }

      worker.onerror = (error) => {
        reject(`Worker error: ${error.message}`)
      }

      worker.postMessage(message)
    })
  }
}
